import * as z from 'zod';

export const SetComponentSettingsRequest = z.object({
  componentId: z.string().describe('The component Id').max(36),
  options: z
    .object({
      settings: z
        .record(z.string(), z.any())
        .describe("The data we'd like to store for this component")
        .optional()
        .nullable(),
      state: z.enum(['NR', 'SAVED', 'PUBLISHED']).optional(),
      host: z.enum(['NA', 'VIEWER', 'BUSINESS_MANAGER', 'ONE_APP']).optional(),
      translations: z
        .array(
          z.object({
            languageKey: z
              .object({
                languageCode: z.string().describe('e.g. en').optional(),
                regionCode: z.string().describe('e.g. us').optional(),
                scriptVariant: z.string().describe("default: ''").optional(),
              })
              .describe("The key of the translations' language")
              .optional(),
            translations: z
              .record(z.string(), z.string())
              .describe('Key-value pair mapping of textual assets')
              .optional(),
          })
        )
        .optional(),
      states: z.array(z.enum(['NR', 'SAVED', 'PUBLISHED'])).optional(),
    })
    .optional(),
});
export const SetComponentSettingsResponse = z.object({
  settings: z
    .record(z.string(), z.any())
    .describe('Echo of the component settings')
    .optional()
    .nullable(),
  host: z
    .enum(['NA', 'VIEWER', 'BUSINESS_MANAGER', 'ONE_APP'])
    .describe('Where this data is relevant')
    .optional(),
  translations: z
    .array(
      z.object({
        languageKey: z
          .object({
            languageCode: z.string().describe('e.g. en').optional(),
            regionCode: z.string().describe('e.g. us').optional(),
            scriptVariant: z.string().describe("default: ''").optional(),
          })
          .describe("The key of the translations' language")
          .optional(),
        translations: z
          .record(z.string(), z.string())
          .describe('Key-value pair mapping of textual assets')
          .optional(),
      })
    )
    .optional(),
  states: z.array(z.enum(['NR', 'SAVED', 'PUBLISHED'])).optional(),
});
export const GetComponentSettingsRequest = z.object({
  componentId: z
    .string()
    .describe(
      'The component we want data for. Component ids can be from the site or oneApp'
    )
    .min(1)
    .max(36),
  options: z
    .object({
      state: z.enum(['NR', 'SAVED', 'PUBLISHED']).optional(),
      host: z.enum(['NA', 'VIEWER', 'BUSINESS_MANAGER', 'ONE_APP']).optional(),
      languageKey: z
        .object({
          languageCode: z.string().describe('e.g. en').optional(),
          regionCode: z.string().describe('e.g. us').optional(),
          scriptVariant: z.string().describe("default: ''").optional(),
        })
        .describe(
          'The language key of the translation (allow filtering translations explicitly)'
        )
        .optional(),
      includeGlobalSettings: z
        .boolean()
        .describe('Whether it is critical to get Global Settings as well')
        .optional(),
    })
    .optional(),
});
export const GetComponentSettingsResponse = z.object({
  settings: z
    .record(z.string(), z.any())
    .describe('Component settings for the requested component id')
    .optional()
    .nullable(),
  state: z
    .enum(['NR', 'SAVED', 'PUBLISHED'])
    .describe('The data state of the response')
    .optional(),
  host: z
    .enum(['NA', 'VIEWER', 'BUSINESS_MANAGER', 'ONE_APP'])
    .describe('Where this data is relevant')
    .optional(),
  translations: z
    .record(z.string(), z.string())
    .describe('based on x-wix-language or fallback')
    .optional(),
  globalSettings: z
    .object({
      state: z
        .enum(['NR', 'SAVED', 'PUBLISHED'])
        .describe('The state of the component')
        .optional(),
      host: z
        .enum(['NA', 'VIEWER', 'BUSINESS_MANAGER', 'ONE_APP'])
        .describe('Where this data is relevant')
        .optional(),
      settings: z
        .record(z.string(), z.any())
        .describe('The app global settings')
        .optional()
        .nullable(),
      translations: z
        .record(z.string(), z.string())
        .describe(
          'Translatable textual assets - not using a collection because this type only the get flow (which should return a single value)'
        )
        .optional(),
    })
    .describe('Optional. The global app settings')
    .optional(),
});
export const SetGlobalSettingsRequest = z.object({
  options: z
    .object({
      settings: z
        .record(z.string(), z.any())
        .describe('The global settings for the app')
        .optional()
        .nullable(),
      componentSettings: z
        .array(
          z.object({
            state: z.enum(['NR', 'SAVED', 'PUBLISHED']).optional(),
            host: z
              .enum(['NA', 'VIEWER', 'BUSINESS_MANAGER', 'ONE_APP'])
              .optional(),
            componentId: z
              .string()
              .describe(
                'The component we want data for. Component ids can be from the site or oneApp'
              )
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            settings: z
              .record(z.string(), z.any())
              .describe('The component settings')
              .optional()
              .nullable(),
            translations: z
              .array(
                z.object({
                  languageKey: z
                    .object({
                      languageCode: z.string().describe('e.g. en').optional(),
                      regionCode: z.string().describe('e.g. us').optional(),
                      scriptVariant: z
                        .string()
                        .describe("default: ''")
                        .optional(),
                    })
                    .describe("The key of the translations' language")
                    .optional(),
                  translations: z
                    .record(z.string(), z.string())
                    .describe('Key-value pair mapping of textual assets')
                    .optional(),
                })
              )
              .optional(),
          })
        )
        .optional(),
      state: z.enum(['NR', 'SAVED', 'PUBLISHED']).optional(),
      host: z.enum(['NA', 'VIEWER', 'BUSINESS_MANAGER', 'ONE_APP']).optional(),
      translations: z
        .array(
          z.object({
            languageKey: z
              .object({
                languageCode: z.string().describe('e.g. en').optional(),
                regionCode: z.string().describe('e.g. us').optional(),
                scriptVariant: z.string().describe("default: ''").optional(),
              })
              .describe("The key of the translations' language")
              .optional(),
            translations: z
              .record(z.string(), z.string())
              .describe('Key-value pair mapping of textual assets')
              .optional(),
          })
        )
        .optional(),
      states: z.array(z.enum(['NR', 'SAVED', 'PUBLISHED'])).optional(),
    })
    .optional(),
});
export const SetGlobalSettingsResponse = z.object({
  settings: z
    .record(z.string(), z.any())
    .describe('The data we have not for the App')
    .optional()
    .nullable(),
  componentSettings: z
    .array(
      z.object({
        state: z
          .enum(['NR', 'SAVED', 'PUBLISHED'])
          .describe('The state of the component')
          .optional(),
        host: z
          .enum(['NA', 'VIEWER', 'BUSINESS_MANAGER', 'ONE_APP'])
          .describe('Where this data is relevant')
          .optional(),
        componentId: z
          .string()
          .describe(
            'The component we want data for. Component ids can be from the site or oneApp'
          )
          .min(1)
          .max(36)
          .optional()
          .nullable(),
        settings: z
          .record(z.string(), z.any())
          .describe('The component settings')
          .optional()
          .nullable(),
        translations: z
          .array(
            z.object({
              languageKey: z
                .object({
                  languageCode: z.string().describe('e.g. en').optional(),
                  regionCode: z.string().describe('e.g. us').optional(),
                  scriptVariant: z.string().describe("default: ''").optional(),
                })
                .describe("The key of the translations' language")
                .optional(),
              translations: z
                .record(z.string(), z.string())
                .describe('Key-value pair mapping of textual assets')
                .optional(),
            })
          )
          .optional(),
      })
    )
    .optional(),
  host: z
    .enum(['NA', 'VIEWER', 'BUSINESS_MANAGER', 'ONE_APP'])
    .describe('Where this data is relevant')
    .optional(),
  translations: z
    .array(
      z.object({
        languageKey: z
          .object({
            languageCode: z.string().describe('e.g. en').optional(),
            regionCode: z.string().describe('e.g. us').optional(),
            scriptVariant: z.string().describe("default: ''").optional(),
          })
          .describe("The key of the translations' language")
          .optional(),
        translations: z
          .record(z.string(), z.string())
          .describe('Key-value pair mapping of textual assets')
          .optional(),
      })
    )
    .optional(),
  states: z.array(z.enum(['NR', 'SAVED', 'PUBLISHED'])).optional(),
});
export const GetGlobalSettingsRequest = z.object({
  options: z
    .object({
      state: z.enum(['NR', 'SAVED', 'PUBLISHED']).optional(),
      host: z.enum(['NA', 'VIEWER', 'BUSINESS_MANAGER', 'ONE_APP']).optional(),
      includeComponentSettings: z
        .boolean()
        .describe('If component settings data should also be returned')
        .optional(),
      languageKey: z
        .object({
          languageCode: z.string().describe('e.g. en').optional(),
          regionCode: z.string().describe('e.g. us').optional(),
          scriptVariant: z.string().describe("default: ''").optional(),
        })
        .describe(
          'The language key of the translation (allow filtering translations explicitly)'
        )
        .optional(),
    })
    .optional(),
});
export const GetGlobalSettingsResponse = z.object({
  state: z
    .enum(['NR', 'SAVED', 'PUBLISHED'])
    .describe('The state of the app')
    .optional(),
  host: z
    .enum(['NA', 'VIEWER', 'BUSINESS_MANAGER', 'ONE_APP'])
    .describe('Where this data is relevant')
    .optional(),
  settings: z
    .record(z.string(), z.any())
    .describe('The global app settings')
    .optional()
    .nullable(),
  componentSettings: z
    .array(
      z.object({
        state: z
          .enum(['NR', 'SAVED', 'PUBLISHED'])
          .describe('The state of the component')
          .optional(),
        host: z
          .enum(['NA', 'VIEWER', 'BUSINESS_MANAGER', 'ONE_APP'])
          .describe('Where this data is relevant')
          .optional(),
        componentId: z
          .string()
          .describe(
            'The component we want data for. Component ids can be from the site or oneApp'
          )
          .min(1)
          .max(36)
          .optional()
          .nullable(),
        settings: z
          .record(z.string(), z.any())
          .describe('The component settings')
          .optional()
          .nullable(),
        translations: z
          .record(z.string(), z.string())
          .describe(
            'Translatable textual assets - not using a collection because this type only the get flow (which should return a single value)'
          )
          .optional(),
      })
    )
    .optional(),
  translations: z
    .record(z.string(), z.string())
    .describe('based on x-wix-language or fallback')
    .optional(),
});
